import { isDemoMode } from "@/lib/demo/demoMode";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  createKnowledgeItem,
  markdownToFields,
  type KnowledgeItem,
} from "./model";

interface Props {
  items: KnowledgeItem[];
  loading: boolean;
  onSave: (item: KnowledgeItem) => Promise<KnowledgeItem>;
}

interface EditableItem {
  key: string;
  item: KnowledgeItem;
}

const newInstructions = (): KnowledgeItem => ({
  ...createKnowledgeItem("instructions"),
  id: "instructions",
  name: "AI instructions",
  responseLength: "default",
});

const sectionHeading = (id: string, title: string, saving: boolean, description?: string, actions?: ReactNode) => (
  <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
    <div className="min-w-0">
      <h2 id={id} className="font-heading text-base font-semibold text-foreground">{title}</h2>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
    </div>
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {saving && <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving</span>}
      {actions}
    </div>
  </div>
);

function normalizeInstructions(item: KnowledgeItem): KnowledgeItem {
  if (!/^##\s+/m.test(item.content)) return item;
  const fields = markdownToFields(item.content);
  const read = (title: string) => fields.find(field => field.title.trim().toLocaleLowerCase() === title)?.content.trim() ?? "";
  const toneText = read("tone of voice").toLocaleLowerCase();
  const lengthText = read("response length").toLocaleLowerCase();
  const tone = toneText.includes("professional") ? "professional"
    : toneText.includes("playful") ? "playful"
    : toneText.includes("concise") ? "concise"
    : toneText ? "friendly"
    : item.tone;
  const responseLength = lengthText.includes("brief") ? "brief"
    : lengthText.includes("balanced") ? "balanced"
    : lengthText.includes("detailed") ? "detailed"
    : lengthText ? "default"
    : item.responseLength;
  return {
    ...item,
    tone,
    responseLength,
    language: read("language preference") || item.language,
    content: read("additional instructions"),
    avoid: read("things to avoid") || item.avoid,
    handover: read("when to hand over to staff") || item.handover,
  };
}

export default function AIInstructionsSettings({ items, loading, onSave }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const initialInstructions = normalizeInstructions(items.find(item => item.type === "instructions") ?? newInstructions());
  const [instructions, setInstructions] = useState<EditableItem>({ key: "ai-settings", item: initialInstructions });
  const [savingKeys, setSavingKeys] = useState<string[]>([]);
  const resolved = useRef(new Map<string, KnowledgeItem>([["ai-settings", initialInstructions]]));

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && rootRef.current?.contains(active)) active.blur();
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const replaceSaved = (key: string, saved: KnowledgeItem) => {
    resolved.current.set(key, saved);
    setInstructions(current => ({ ...current, item: { ...current.item, id: saved.id, updatedAt: saved.updatedAt, status: saved.status } }));
  };

  const save = async (entry: EditableItem): Promise<KnowledgeItem | null> => {
    setSavingKeys(current => current.includes(entry.key) ? current : [...current, entry.key]);
    try {
      const previous = resolved.current.get(entry.key);
      const saved = await onSave({
        ...entry.item,
        id: previous?.id ?? entry.item.id,
        updatedAt: previous?.updatedAt,
      });
      replaceSaved(entry.key, saved);
      return saved;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "This setting could not be saved.");
      return null;
    } finally {
      setSavingKeys(current => current.filter(key => key !== entry.key));
    }
  };

  const setInstruction = (patch: Partial<KnowledgeItem>) => setInstructions(current => ({ ...current, item: { ...current.item, ...patch } }));
  const saveInstructions = async () => {
    if (await save(instructions)) toast.success("AI settings saved");
  };

  if (loading) return <div className="flex flex-1 items-center justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading AI instructions" /></div>;

  const saveButton = <Button type="button" size="sm" className="standard-action-button" disabled={savingKeys.includes(instructions.key)} onClick={() => void saveInstructions()}>Save changes</Button>;

  return (
    <div ref={rootRef} className="min-h-0 flex-1 overflow-y-auto py-6">
      {/* Disabled as a group in the public demo: no field or save button works. */}
      <fieldset disabled={isDemoMode()} className="m-0 w-full min-w-0 space-y-8 border-0 p-0">
        <section aria-labelledby="ai-voice-heading" className="rounded-xl border bg-card p-6 shadow-sm">
          {sectionHeading("ai-voice-heading", "Voice and style", savingKeys.includes(instructions.key), "Set how your AI sounds and responds to customers.", saveButton)}
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ai-tone">Tone of voice</Label>
                <Select value={instructions.item.tone ?? "friendly"} onValueChange={value => setInstruction({ tone: value })}>
                  <SelectTrigger id="ai-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly and warm</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="playful">Playful and casual</SelectItem>
                    <SelectItem value="concise">Concise and direct</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ai-response-length">Response length</Label>
                <Select value={instructions.item.responseLength ?? "default"} onValueChange={value => setInstruction({ responseLength: value })}>
                  <SelectTrigger id="ai-response-length"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Let AI decide</SelectItem>
                    <SelectItem value="brief">Brief</SelectItem>
                    <SelectItem value="balanced">Balanced</SelectItem>
                    <SelectItem value="detailed">Detailed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ai-language">Language preference</Label>
              <Input id="ai-language" value={instructions.item.language ?? ""} onChange={event => setInstruction({ language: event.target.value })} placeholder="Match the customer, or specify a preferred language" />
            </div>
          </div>
        </section>

        <section aria-labelledby="ai-rules-heading" className="rounded-xl border bg-card p-6 shadow-sm">
          {sectionHeading("ai-rules-heading", "Rules", savingKeys.includes(instructions.key), "Give your AI business-wide rules to follow and things to stay away from.", saveButton)}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ai-additional-instructions">Additional instructions</Label>
              <Textarea id="ai-additional-instructions" className="min-h-32 resize-y" value={instructions.item.content} onChange={event => setInstruction({ content: event.target.value })} placeholder="Add business-wide rules for AI-generated replies." />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ai-avoid">Things to avoid</Label>
              <Textarea id="ai-avoid" className="min-h-24 resize-y" value={instructions.item.avoid ?? ""} onChange={event => setInstruction({ avoid: event.target.value })} placeholder="Topics, claims, or phrases the AI should avoid" />
            </div>
          </div>
        </section>

      </fieldset>
    </div>
  );
}
